import { Inject, Injectable } from '@angular/core';
import { take } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ContactTracingService } from '../../contact-tracing/services/contact-tracing.service';
import { LayoutService } from '@shared/services/layout.service';
import { WINDOW } from '@shared/services/window.service';

export interface CoordinatesDto {
  lat: number;
  lng: number;
}

export enum LOCATION_ERROR {
  ACCESS = 'ACCESS',
  DISTANCE = 'DISTANCE'
}

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  static earthRadius = 3958.75;

  currentLocation: any;
  errorSubject = new Subject<LOCATION_ERROR>();
  error$: Observable<LOCATION_ERROR> = this.errorSubject.asObservable();

  locationSubject = new BehaviorSubject<any>(null);
  location$ = this.locationSubject.asObservable();

  statusSubject = new BehaviorSubject<boolean>(false);
  status$ = this.statusSubject.asObservable();


  constructor(@Inject(WINDOW) private window: Window,
              private contactTracingService: ContactTracingService,
              private layoutService: LayoutService) { }

  checkLocation(qrCodeGeoLocation: CoordinatesDto): Promise<any> {
    return new Promise((resolve, reject) => {
      this.window.navigator.geolocation.getCurrentPosition((res) => {
        const currentLocationDto = {
          lat: res.coords.latitude,
          lng: res.coords.longitude
        }
        this.locationSubject.next(currentLocationDto);
        const distance = this.calcDistanceBetween(currentLocationDto, qrCodeGeoLocation);

        // console.log(`distance `, distance);

        if (distance <= this.layoutService.currentConfig.locationCheck.distance) {
          resolve(true);
        } else {
          resolve(LOCATION_ERROR.DISTANCE);
        }

      },() => {
        resolve(LOCATION_ERROR.ACCESS);
      },{
        maximumAge: 600000,
        enableHighAccuracy: true,
        timeout:10000
      });
    })
  }

  getCurrentLocation() {
    return new Promise((resolve, reject) => {
      this.window.navigator.geolocation.getCurrentPosition((res) => {
        this.locationSubject.next({
          lat: res.coords.latitude,
          lng: res.coords.longitude
        });
        resolve(res);
      },() => {
        resolve(LOCATION_ERROR.ACCESS);
      },{
        enableHighAccuracy: true,
        timeout:10000
      });
    });
  }

  geoPermissions(): Promise<any> {
    return this.window.navigator.permissions.query({ name: 'geolocation' })
  }

  calcDistanceBetween(first: CoordinatesDto, second: CoordinatesDto) {
    const latDiff = LocationService.degToRad(second.lat - first.lat);
    const lngDiff = LocationService.degToRad(second.lng - first.lng);

    const a =
      Math.sin(latDiff / 2)
      * Math.sin(latDiff / 2)
      + Math.cos(LocationService.degToRad(first.lat))
      * Math.cos(LocationService.degToRad(second.lat))
      * Math.sin(lngDiff / 2) * Math.sin(lngDiff / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = LocationService.earthRadius * c;

    const meterConversion = 1609;

    return distance * meterConversion;
  }

  static degToRad(deg: number): number {
    return deg * (Math.PI / 180.0);
  }

  static radToDeg(rad: number): number {
    return rad * (180.0 / Math.PI);
  }
}

